import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useRef } from 'react';
import styled from 'styled-components';
import newcolors from 'styles/new-design/colors';
import { DialogCenter, DialogLeft, DialogPage } from './DialogTypes';

type DialogProps = {
  type: string;
  title: string;
  titleId?: string;
  width?: number;
  height?: number;
  focusDialog?: boolean;
  children: JSX.Element | JSX.Element[];
  onModalClose: (() => void) | null;
};

const Overlay = styled(DialogPrimitive.Overlay)`
  position: fixed;
  inset: 0;
  background: ${newcolors.backdrop};
  z-index: 1000;
`;

const Content = styled(DialogPrimitive.Content)<{ $verticallyCenter: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1001;
  outline: none;
  display: ${(props) => (props.$verticallyCenter ? 'flex' : 'block')};
  align-items: ${(props) => (props.$verticallyCenter ? 'center' : 'initial')};
  justify-content: ${(props) => (props.$verticallyCenter ? 'center' : 'initial')};
`;

const VisuallyHiddenTitle = styled(DialogPrimitive.Title)`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const Dialog = ({
  type,
  title,
  titleId,
  width,
  height,
  focusDialog,
  onModalClose,
  children,
}: DialogProps): JSX.Element => {
  const contentRef = useRef<HTMLDivElement>(null);

  const handleBackdropPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onModalClose?.();
    }
  };

  const handleEscapeKeyDown = (event: Event) => {
    event.preventDefault();
    onModalClose?.();
  };

  const handleOpenAutoFocus = (event: Event) => {
    if (focusDialog) {
      event.preventDefault();
      contentRef.current?.focus();
    }
  };

  return (
    <DialogPrimitive.Root open onOpenChange={() => undefined}>
      <DialogPrimitive.Portal>
        <Overlay />
        <Content
          aria-describedby={undefined}
          ref={contentRef}
          tabIndex={-1}
          $verticallyCenter={type === 'center'}
          onPointerDown={handleBackdropPointerDown}
          onEscapeKeyDown={handleEscapeKeyDown}
          onOpenAutoFocus={handleOpenAutoFocus}
        >
          <VisuallyHiddenTitle>{title || titleId || 'Dialog'}</VisuallyHiddenTitle>
          {type === 'center' && (
            <DialogCenter width={width} height={height} onModalClose={onModalClose}>
              {children}
            </DialogCenter>
          )}
          {type === 'left' && <DialogLeft onModalClose={onModalClose}>{children}</DialogLeft>}
          {type === 'page' && <DialogPage onModalClose={onModalClose}>{children}</DialogPage>}
        </Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
